import React from 'react';
import { useTranslation } from 'localization';
import './LocaleTabsMolecule.scss';

export interface LocaleTabsMoleculeProps {
  locales: string[];
  defaultLocale: string;
  activeLocale: string;
  onLocaleChange: (locale: string) => void;
  /** When provided, non-default locale tabs render a remove button. Default locale never gets a remove button. */
  onRemove?: (locale: string) => void;
  className?: string;
}

const LOCALE_DISPLAY: Record<string, string> = {
  'en-US': 'English',
  'fr-FR': 'Français',
  'es-ES': 'Español',
  'de-DE': 'Deutsch',
  'pt-BR': 'Português',
  'it-IT': 'Italiano',
  'nl-NL': 'Nederlands',
  'ja-JP': '日本語',
  'zh-CN': '中文',
};

// 2-letter country code shown before the language name in the tab —
// matches the "US English" / "FR Français" pattern in the new design.
const LOCALE_COUNTRY: Record<string, string> = {
  'en-US': 'US',
  'fr-FR': 'FR',
  'es-ES': 'ES',
  'de-DE': 'DE',
  'pt-BR': 'PT',
  'it-IT': 'IT',
  'nl-NL': 'NL',
  'ja-JP': 'JP',
  'zh-CN': 'CN',
};

function getLocaleDisplay(locale: string): string {
  return LOCALE_DISPLAY[locale] ?? locale;
}

function getLocaleCountry(locale: string): string {
  return LOCALE_COUNTRY[locale] ?? locale.split('-')[1]?.toUpperCase() ?? locale.slice(0, 2).toUpperCase();
}

const LocaleTabsMolecule: React.FC<LocaleTabsMoleculeProps> = ({
  locales,
  defaultLocale,
  activeLocale,
  onLocaleChange,
  onRemove,
  className = '',
}) => {
  const { t } = useTranslation();

  const classes = ['marketplace-locale-tabs', className].filter(Boolean).join(' ');

  return (
    <div className={classes} role="tablist" aria-label={t('marketplace.publishers.localeTabs.ariaLabel')}>
      {locales.map((locale) => (
        <span
          key={locale}
          role="none"
          className="marketplace-locale-tabs__tab-wrapper"
          data-locale={locale}
        >
          <button
            type="button"
            role="tab"
            aria-selected={activeLocale === locale}
            className={[
              'marketplace-locale-tabs__tab',
              activeLocale === locale ? 'marketplace-locale-tabs__tab--active' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={() => onLocaleChange(locale)}
          >
            <span className="marketplace-locale-tabs__country" aria-hidden="true">
              {getLocaleCountry(locale)}
            </span>
            <span className="marketplace-locale-tabs__name">
              {getLocaleDisplay(locale)}
            </span>
            {locale === defaultLocale && (
              <span className="marketplace-locale-tabs__default-badge">
                {t('marketplace.publishers.localeTabs.defaultBadge')}
              </span>
            )}
          </button>
          {onRemove && locale !== defaultLocale && (
            <button
              type="button"
              className="marketplace-locale-tabs__remove-btn"
              title={t('marketplace.publishers.localeTabs.removeLocale')}
              aria-label={t('marketplace.publishers.localeTabs.removeLocale')}
              onClick={(e) => {
                e.stopPropagation();
                onRemove(locale);
              }}
            >
              <i className="pi pi-times" aria-hidden="true" />
            </button>
          )}
        </span>
      ))}
    </div>
  );
};

LocaleTabsMolecule.displayName = 'LocaleTabsMolecule';

export default LocaleTabsMolecule;
